import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";
import MainLayout from "../../layouts/Layout";
import { FileOutlined } from "@ant-design/icons";
import { useSecureService } from "../../store/service";
import { useAuth } from "../../hooks/useAuth";
import { Button, Modal, Input, message } from "antd";
import { useLanguage } from "../../contexts/LanguageContext";
import { Shape, DrawingTool } from "../../components/VideoPlayer/VideoPlayer";
import styles from "./Projects.module.scss";
import AddNewFileModal from '../../modals/addNewFileModal';
import FooterActions from './FooterActions';
import FileVersions from './FileVersions';
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";

import ApprovalModal from '../../modals/approvalModal';
import StatusSelector from '../../components/StatusSelector/StatusSelector';


// New components
import CommentSection, { CommentType } from '../../components/CommentSection/CommentSection';
import VideoPlayerSection from '../../components/VideoPlayerSection/VideoPlayerSection';
import { useFileOperations } from '../../hooks/useFileOperations';

interface FileDetailProps {
  fileDetail?: any;
  onBack?: () => void;
  breadcrumbPathParts: any[];
  bclocation: any;
}

const FileDetail: React.FC<FileDetailProps> = ({ fileDetail: propFileDetail, onBack ,breadcrumbPathParts, bclocation}) => {
  const { t } = useLanguage();
  const { projectName } = useParams<{ projectName: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, canComment, canResolve, canShowAllComments } = useAuth();
  const { getFileById, getEntryByPath, changeStatus } = useSecureService();
  
  // Main file state
  const [fileDetail, setFileDetail] = useState<any>(propFileDetail || null);
  const [loading, setLoading] = useState(!propFileDetail);
  const [isDrawingMode] = useState(true);

  // Version states
  const [versions, setVersions] = useState<any[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number>(1);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string>('');
  const [videoLoading, setVideoLoading] = useState(false);

  // Compare player states
  const [selectedVersionCompare, setSelectedVersionCompare] = useState<number | null>(null);
  const [currentVideoUrlCompare, setCurrentVideoUrlCompare] = useState<string>('');
  const [, setVideoLoadingCompare] = useState(false);

  // Drawing states - Main player
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [drawingTool, setDrawingTool] = useState<DrawingTool>('line');
  const [drawingColor, setDrawingColor] = useState('#df4b26');
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);

  // Drawing states - Compare player
  const [shapesCompare, setShapesCompare] = useState<Shape[]>([]);
  const [selectedShapeIdCompare, setSelectedShapeIdCompare] = useState<string | null>(null);

  // Undo/Redo states - Main player
  const [drawingHistory, setDrawingHistory] = useState<Array<{
    shapes: Shape[];
    selectedShapeId: string | null;
  }>>([{ shapes: [], selectedShapeId: null }]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);

  // Undo/Redo states - Compare player
  const [drawingHistoryCompare, setDrawingHistoryCompare] = useState<Array<{
    shapes: Shape[];
    selectedShapeId: string | null;
  }>>([{ shapes: [], selectedShapeId: null }]);
  const [currentHistoryIndexCompare, setCurrentHistoryIndexCompare] = useState(0);

  // Text tool states
  const [isTextModalVisible, setIsTextModalVisible] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });

  // Video time states
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [currentFps] = useState(0);
  const [currentVideoTimeCompare, setCurrentVideoTimeCompare] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  // Range states
  const [selectedRange, setSelectedRange] = useState<[number, number] | null>(null);
  const [externalRange, setExternalRange] = useState<[number, number] | null>(null);
  const [selectedRangeCompare, setSelectedRangeCompare] = useState<[number, number] | null>(null);

  // Frame states
  const [currentFrameIndex, setCurrentFrameIndex] = useState<number | null>(null);
  const [currentFrameIndexCompare, setCurrentFrameIndexCompare] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isPausedCompare, setIsPausedCompare] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState<number | null>(null);
  const [selectedFrameRange, setSelectedFrameRange] = useState<[number, number] | null>(null);
  const [selectedFrameCompare, setSelectedFrameCompare] = useState<number | null>(null);
  const [selectedFrameRangeCompare, setSelectedFrameRangeCompare] = useState<[number, number] | null>(null);
  const [currentFrameRange, setCurrentFrameRange] = useState<string>('');
  const [currentFrameRangeCompare, setCurrentFrameRangeCompare] = useState<string>('');

  // Compare mode states
  const [compareMode, setCompareMode] = useState(false);
  const [activePlayer, setActivePlayer] = useState(1);
  const [footerMounted, setFooterMounted] = useState(false);
  
  // Comment states
  const [showAllComments, setShowAllComments] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState<number | null>(null);

  // Drawing UI states
  const [closeDrawingPopover, setCloseDrawingPopover] = useState(false);
  // Status change states
// Status change states - sadece approved için modal
// const [statusLoading, setStatusLoading] = useState(false);
// const [selectedStatusForModal, setSelectedStatusForModal] = useState<string>('NeedsReview');
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [isDrawingToolboxOpen, setIsDrawingToolboxOpen] = useState(true);

  // Modal states
  const [isAddNewFileModalOpen, setIsAddNewFileModalOpen] = useState(false);
  // Footer actions states - seçili version için
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [selectedItemsDetails, setSelectedItemsDetails] = useState<Array<{ id: number; name: string; entryType: string; status?: string; share?: any[]; thumbnail?: any }>>([]);

  // Use file operations hook
  const { handleDelete, handleDownload, handleCopyUrl } = useFileOperations({
    fileDetail,
    selectedVersion: compareMode ? (selectedVersionCompare ?? selectedVersion) : selectedVersion,
    versions,
    location
  });

  const FPS = 30;
  const isExternalEntryByUrl = useMemo(
    () => location.pathname.startsWith('/external-projects') || location.pathname.includes('/ext/'),
    [location.pathname]
  );

  const fileDetailThemeVars = useMemo(
    () =>
      ({
        '--app-accent-color': isExternalEntryByUrl ? '#9B86BC' : '#00C271',
        '--app-accent-color-soft': isExternalEntryByUrl ? '#9B86BC80' : '#00C27180',
        '--app-accent-hover': isExternalEntryByUrl ? '#AB96CC' : '#00B366',
        '--app-accent-active': isExternalEntryByUrl ? '#8A74AD' : '#00A35C',
        '--app-accent-rgb': isExternalEntryByUrl ? '155, 134, 188' : '0, 194, 113',
        '--app-accent-secondary': isExternalEntryByUrl ? '#B7A3D6' : '#4CAF50',
        '--app-accent-secondary-rgb': isExternalEntryByUrl ? '183, 163, 214' : '76, 175, 80',
        '--app-divider-color': isExternalEntryByUrl ? '#6A5A86' : '#00462C',
        '--app-glow-color': isExternalEntryByUrl ? 'rgba(155, 134, 188, 0.5)' : 'rgba(15, 111, 70, 0.5)',
      } as Record<string, string>),
    [isExternalEntryByUrl]
  );

  // Footer mount effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setFooterMounted(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Fetch file detail effect - propFileDetail varsa (ProjectDetail'den geliyorsa) tekrar getEntryByPath çağırma
  useEffect(() => {
    const fetchFileDetail = async () => {
      let fullPath = location.pathname.substring(1);
      if (fullPath.endsWith('/versions')) {
        fullPath = fullPath.replace('/versions', '');
      }

      // Parent (ProjectDetail) zaten entry'yi çekip geçirdiyse aynı path için tekrar istek atma
      if (propFileDetail) {
        if (propFileDetail.id) {
          try {
            const fileResponse = await getFileById(propFileDetail.id);
            if (fileResponse.success && fileResponse.data) {
              setFileDetail({ ...fileResponse.data, entryType: propFileDetail.entryType, path: propFileDetail.path });
            } else {
              setFileDetail(propFileDetail);
            }
          } catch {
            setFileDetail(propFileDetail);
          }
        } else {
          setFileDetail(propFileDetail);
        }
        setLoading(false);
        resetAllStates();
        return;
      }

      if (fullPath) {
        setLoading(true);
        try {
          const entryResponse = await getEntryByPath(fullPath);
          if (entryResponse.success && entryResponse.data) {
            const entryData = entryResponse.data;
            if (entryData.id) {
              const fileResponse = await getFileById(entryData.id);
              if (fileResponse.success && fileResponse.data) {
                setFileDetail({ ...fileResponse.data, entryType: entryData.entryType, path: entryData.path });
              } else {
                setFileDetail(entryData);
              }
            } else {
              setFileDetail(entryData);
            }
            resetAllStates();
          } else {
            setFileDetail(null);
          }
        } catch (error) {
          setFileDetail(null);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchFileDetail();
  }, [propFileDetail, projectName, location.pathname, user?.role]);

  // Reset all states helper
  const resetAllStates = () => {
        setCurrentVideoTime(0);
        setCurrentFrameIndex(null);
        setSelectedFrame(null);
        setSelectedFrameRange(null);
        setCurrentFrameRange('');
    setSelectedRange(null);
        setShapes([]);
        setIsPaused(false);
        setSelectedFrameCompare(null);
        setSelectedFrameRangeCompare(null);
    setSelectedRangeCompare(null);
        setShapesCompare([]);
        setCurrentVideoTimeCompare(0);
        setCurrentFrameIndexCompare(null);
        setIsPausedCompare(false);
        // Comment seçim state'lerini sıfırla
        setSelectedCommentId(null);
        setActivePlayer(1);
    };

  // Version management effect
  useEffect(() => {
    if (fileDetail?.versions) {
      setVersionsLoading(true);
      try {
        const fileVersions = fileDetail.versions;
        
        if (fileVersions && fileVersions.length > 0) {
          setVersions(fileVersions);
          
          const hash = window.location.hash.replace('#', '');
          let targetVersionId = fileVersions[0].id || 1;
          
          if (hash) {
            let targetVersion = fileVersions.find((v: any) => 
              v.name && v.name.toLowerCase() === hash.toLowerCase()
            );
            
            if (targetVersion) {
              targetVersionId = targetVersion.id;
            } else {
              targetVersion = fileVersions.find((v: any) => {
                if (v.name) {
                  const versionNumber = hash.toLowerCase().replace('v', '');
                  const versionName = v.name.toLowerCase().replace('v', '');
                  return versionName === versionNumber || v.name.toLowerCase() === hash.toLowerCase();
                }
                return false;
              });
              
                              if (targetVersion) {
                  targetVersionId = targetVersion.id;
                } else {
                targetVersion = fileVersions.find((v: any) => {
                  if (v.name) {
                    return v.name.toLowerCase().includes(hash.toLowerCase()) || 
                           hash.toLowerCase().includes(v.name.toLowerCase());
                  }
                  return false;
                });
                
                                  if (targetVersion) {
                    targetVersionId = targetVersion.id;
                  } else {
                    targetVersion = fileVersions.find((v: any) => {
                      if (v.path) {
                        return v.path.toLowerCase().includes(hash.toLowerCase());
                      }
                      return false;
                    });
                    
                    if (targetVersion) {
                      targetVersionId = targetVersion.id;
                    }
                  }
              }
            }
          } else {
            // Hash yoksa /versions'a gitmek yerine en son versiyonu seç
            if (fileVersions.length > 0) {
              let latest = fileVersions[0];
              try {
                latest = fileVersions.reduce((acc: any, cur: any) => {
                  const accDate = acc?.createdAt ? new Date(acc.createdAt).getTime() : 0;
                  const curDate = cur?.createdAt ? new Date(cur.createdAt).getTime() : 0;
                  if (curDate === accDate) {
                    // Tarih eşit veya yoksa id'ye göre kıyasla
                    return (cur?.id || 0) > (acc?.id || 0) ? cur : acc;
                  }
                  return curDate > accDate ? cur : acc;
                }, fileVersions[0]);
              } catch (_) {
                // ignore
              }
              targetVersionId = latest?.id || fileVersions[fileVersions.length - 1].id || 1;
            }
          }
          
          setSelectedVersion(targetVersionId);
        } else {
          setVersions([]);
          setSelectedVersion(1);
        }
      } catch (error) {
        setVersions([]);
        setSelectedVersion(1);
      } finally {
        setVersionsLoading(false);
      }
    } else {
      setVersions([]);
      setSelectedVersion(1);
      setVersionsLoading(false);
    }
  }, [fileDetail, navigate]);

  // Hash change listener
  useEffect(() => {
    const handleHashChange = () => {
      if (fileDetail?.versions && fileDetail.versions.length > 0) {
        const hash = window.location.hash.replace('#', '');
        
        if (hash) {
          let targetVersion = fileDetail.versions.find((v: any) => 
            v.name && v.name.toLowerCase() === hash.toLowerCase()
          );
          
          if (targetVersion) {
            setSelectedVersion(targetVersion.id);
            return;
          }
          
          targetVersion = fileDetail.versions.find((v: any) => {
            if (v.name) {
              return v.name.toLowerCase().includes(hash.toLowerCase()) || 
                     hash.toLowerCase().includes(v.name.toLowerCase());
            }
            return false;
          });
          
          if (targetVersion) {
            setSelectedVersion(targetVersion.id);
          } else {
            targetVersion = fileDetail.versions.find((v: any) => {
              if (v.path) {
                return v.path.toLowerCase().includes(hash.toLowerCase());
              }
              return false;
            });
            
            if (targetVersion) {
              setSelectedVersion(targetVersion.id);
            }
          }
        }
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [fileDetail]);

  // Update video URL when version changes
  useEffect(() => {
    let newUrl = '';
    if (selectedVersion && versions.length > 0) {
      const selectedVersionData = versions.find((v: any) => v.id === selectedVersion);
      if (selectedVersionData) {
        newUrl = selectedVersionData.url || selectedVersionData.fileUrl || selectedVersionData.downloadUrl || selectedVersionData.path || '';
        
        // Footer actions için selectedItems güncelle
        setSelectedItems(new Set([selectedVersion]));
        setSelectedItemsDetails([{
          id: selectedVersion,
          name: selectedVersionData.name || fileDetail?.name || 'Version',
          entryType: 'Version', // Version seçiliyken entryType Version olmalı
          status: selectedVersionData.status || 'NeedsReview', // Version'ın status'u
          share: fileDetail?.share,
          thumbnail: selectedVersionData.thumbnailUrl || fileDetail?.thumbnailUrl
        }]);
      }
    } else if (fileDetail) {
      newUrl = fileDetail.url || fileDetail.fileUrl || fileDetail.downloadUrl || fileDetail.path || '';
      
      // Footer actions için fileDetail'ı selectedItems olarak ayarla
      setSelectedItems(new Set([fileDetail.id]));
      setSelectedItemsDetails([{
        id: fileDetail.id,
        name: fileDetail.name || 'File',
        entryType: fileDetail.entryType || 'File',
        status: fileDetail.status,
        share: fileDetail.share,
        thumbnail: fileDetail.thumbnailUrl
      }]);
    }

    if (newUrl && newUrl !== currentVideoUrl) {
      setVideoLoading(true);
      setCurrentVideoUrl(newUrl);
      setTimeout(() => {
        setVideoLoading(false);
      }, 500);
    }

    // Reset drawing states when version changes
    setShapes([]);
    setSelectedShapeId(null);
    setCurrentFrameIndex(null);
    setSelectedRange(null);
    setExternalRange(null);
  }, [selectedVersion, versions, fileDetail]);

  // Update compare video URL
  useEffect(() => {
    if (!compareMode || !selectedVersionCompare) {
      setCurrentVideoUrlCompare('');
      setVideoLoadingCompare(false);
      return;
    }

    let newUrl = '';
    if (selectedVersionCompare && versions.length > 0) {
      const selectedVersionData = versions.find((v: any) => v.id === selectedVersionCompare);
      if (selectedVersionData) {
        newUrl = selectedVersionData.url || selectedVersionData.fileUrl || selectedVersionData.downloadUrl || selectedVersionData.path || '';
        
        // Compare mode'da footer actions için selectedItems güncelle
        if (compareMode) {
          setSelectedItems(new Set([selectedVersionCompare]));
          setSelectedItemsDetails([{
            id: selectedVersionCompare,
            name: selectedVersionData.name || fileDetail?.name || 'Version',
            entryType: 'Version', // Version seçiliyken entryType Version olmalı
            status: selectedVersionData.status || 'NeedsReview', // Version'ın status'u
            share: fileDetail?.share,
            thumbnail: selectedVersionData.thumbnailUrl || fileDetail?.thumbnailUrl
          }]);
        }
      }
    }

    if (newUrl && newUrl !== currentVideoUrlCompare) {
      setVideoLoadingCompare(true);
      setCurrentVideoUrlCompare(newUrl);
      setTimeout(() => {
        setVideoLoadingCompare(false);
      }, 500);
    }

    // Reset compare player states
    setShapesCompare([]);
    setSelectedShapeIdCompare(null);
    setCurrentFrameIndexCompare(null);
    setSelectedRangeCompare(null);
  }, [selectedVersionCompare, versions, compareMode]);

  // Reset compare mode states
  useEffect(() => {
    if (!compareMode) {
      setSelectedVersionCompare(null);
      setCurrentVideoUrlCompare('');
      setVideoLoadingCompare(false);
      setShapesCompare([]);
      setSelectedShapeIdCompare(null);
      setCurrentFrameIndexCompare(null);
      setSelectedRangeCompare(null);
      
      // Normal mode'a dönerken selectedItems'ı main version'a göre güncelle
      if (selectedVersion && versions.length > 0) {
        const selectedVersionData = versions.find((v: any) => v.id === selectedVersion);
        if (selectedVersionData) {
          setSelectedItems(new Set([selectedVersion]));
          setSelectedItemsDetails([{
            id: selectedVersion,
            name: selectedVersionData.name || fileDetail?.name || 'Version',
            entryType: 'Version', // Version seçiliyken entryType Version olmalı
            status: selectedVersionData.status || 'NeedsReview', // Version'ın status'u
            share: fileDetail?.share,
            thumbnail: selectedVersionData.thumbnailUrl || fileDetail?.thumbnailUrl
          }]);
        }
      } else if (fileDetail) {
        setSelectedItems(new Set([fileDetail.id]));
        setSelectedItemsDetails([{
          id: fileDetail.id,
          name: fileDetail.name || 'File',
          entryType: fileDetail.entryType || 'File',
          status: fileDetail.status,
          share: fileDetail.share,
          thumbnail: fileDetail.thumbnailUrl
        }]);
      }
    }
  }, [compareMode]);

  // Global compare version listener
  useEffect(() => {
    const checkForCompareVersion = () => {
      const globalSelectedVersion = (window as any).selectedVersionForCompare;
      if (globalSelectedVersion) {
        setCompareMode(true);
        setSelectedVersionCompare(globalSelectedVersion);
        (window as any).selectedVersionForCompare = null;
      }
    };

    const interval = setInterval(checkForCompareVersion, 100);
    return () => clearInterval(interval);
  }, []);

  // Frame selection handlers
  const handleFrameSelect = (frame: number) => {
    setSelectedFrame(frame);
    setSelectedFrameRange(null);
  };

  const handleFrameRangeSelect = (start: number, end: number) => {
    setSelectedFrame(null);
    setSelectedFrameRange([start, end]);
  };

  const handleFrameSelectCompare = (frame: number) => {
    setSelectedFrameCompare(frame);
    setSelectedFrameRangeCompare(null);
  };

  const handleFrameRangeSelectCompare = (start: number, end: number) => {
    setSelectedFrameCompare(null);
    setSelectedFrameRangeCompare([start, end]);
  };

  // Range selection handlers
  const handleRangeSelect = (range: [number, number]) => {
    const startFrame = Math.round(range[0] * FPS);
    const endFrame = Math.round(range[1] * FPS);
    
    if (startFrame === endFrame) {
      handleFrameSelect(startFrame);
      setCurrentFrameRange(`${startFrame}`);
    } else {
      handleFrameRangeSelect(startFrame, endFrame);
      setCurrentFrameRange(`${startFrame}-${endFrame}`);
    }
    
    setSelectedRange(range);
    setExternalRange(null);
  };

  const handleRangeSelectCompare = (range: [number, number]) => {
    const startFrame = Math.round(range[0] * FPS);
    const endFrame = Math.round(range[1] * FPS);
    
    if (startFrame === endFrame) {
      handleFrameSelectCompare(startFrame);
  } else {
      handleFrameRangeSelectCompare(startFrame, endFrame);
    }
    
    setSelectedRangeCompare(range);
  };

  // Drawing completion handlers
  const handleDrawingComplete = useCallback(() => {
    const currentState = { shapes: [...shapes], selectedShapeId };
    const lastState = drawingHistory[currentHistoryIndex];
    if (JSON.stringify(currentState) !== JSON.stringify(lastState)) {
      const newHistory = drawingHistory.slice(0, currentHistoryIndex + 1);
      newHistory.push(currentState);
      setDrawingHistory(newHistory);
      setCurrentHistoryIndex(newHistory.length - 1);
    }
  }, [shapes, selectedShapeId, drawingHistory, currentHistoryIndex]);

  const handleDrawingCompleteCompare = useCallback(() => {
    const currentState = { shapes: [...shapesCompare], selectedShapeId: selectedShapeIdCompare };
    const lastState = drawingHistoryCompare[currentHistoryIndexCompare];
    if (JSON.stringify(currentState) !== JSON.stringify(lastState)) {
      const newHistory = drawingHistoryCompare.slice(0, currentHistoryIndexCompare + 1);
      newHistory.push(currentState);
      setDrawingHistoryCompare(newHistory);
      setCurrentHistoryIndexCompare(newHistory.length - 1);
    }
  }, [shapesCompare, selectedShapeIdCompare, drawingHistoryCompare, currentHistoryIndexCompare]);

  // Undo/Redo handlers
  const handleUndo = useCallback(() => {
    if (currentHistoryIndex > 0) {
      const newIndex = currentHistoryIndex - 1;
      const previousState = drawingHistory[newIndex];
      setCurrentHistoryIndex(newIndex);
      setShapes([...previousState.shapes]);
      setSelectedShapeId(previousState.selectedShapeId);
    }
  }, [currentHistoryIndex, drawingHistory]);

  const handleRedo = useCallback(() => {
    if (currentHistoryIndex < drawingHistory.length - 1) {
      const newIndex = currentHistoryIndex + 1;
      const nextState = drawingHistory[newIndex];
      setCurrentHistoryIndex(newIndex);
      setShapes([...nextState.shapes]);
      setSelectedShapeId(nextState.selectedShapeId);
    }
  }, [currentHistoryIndex, drawingHistory]);

  const handleUndoCompare = useCallback(() => {
    if (currentHistoryIndexCompare > 0) {
      const newIndex = currentHistoryIndexCompare - 1;
      const previousState = drawingHistoryCompare[newIndex];
      setCurrentHistoryIndexCompare(newIndex);
      setShapesCompare([...previousState.shapes]);
      setSelectedShapeIdCompare(previousState.selectedShapeId);
    }
  }, [currentHistoryIndexCompare, drawingHistoryCompare]);

  const handleRedoCompare = useCallback(() => {
    if (currentHistoryIndexCompare < drawingHistoryCompare.length - 1) {
      const newIndex = currentHistoryIndexCompare + 1;
      const nextState = drawingHistoryCompare[newIndex];
      setCurrentHistoryIndexCompare(newIndex);
      setShapesCompare([...nextState.shapes]);
      setSelectedShapeIdCompare(nextState.selectedShapeId);
    }
  }, [currentHistoryIndexCompare, drawingHistoryCompare]);

  // Drawing action handlers
  const handleDeleteSelected = () => {
    if (selectedShapeId) {
      const updatedShapes = shapes.filter(shape => shape.id !== selectedShapeId);
      setShapes(updatedShapes);
      setSelectedShapeId(null);
      
      const currentState = { shapes: updatedShapes, selectedShapeId: null };
      const newHistory = drawingHistory.slice(0, currentHistoryIndex + 1);
      newHistory.push(currentState);
      setDrawingHistory(newHistory);
      setCurrentHistoryIndex(newHistory.length - 1);
    }
  };

  const handleDeleteSelectedCompare = () => {
    if (selectedShapeIdCompare) {
      const updatedShapes = shapesCompare.filter(shape => shape.id !== selectedShapeIdCompare);
      setShapesCompare(updatedShapes);
      setSelectedShapeIdCompare(null);
      
      const currentState = { shapes: updatedShapes, selectedShapeId: null };
      const newHistory = drawingHistoryCompare.slice(0, currentHistoryIndexCompare + 1);
      newHistory.push(currentState);
      setDrawingHistoryCompare(newHistory);
      setCurrentHistoryIndexCompare(newHistory.length - 1);
    }
  };

  const handleChangeSelectedColor = (newColor: string) => {
    if (selectedShapeId) {
      const updatedShapes = shapes.map(shape => 
        shape.id === selectedShapeId 
          ? { ...shape, color: newColor }
          : shape
      );
      setShapes(updatedShapes);
      
      const currentState = { shapes: updatedShapes, selectedShapeId };
      const newHistory = drawingHistory.slice(0, currentHistoryIndex + 1);
      newHistory.push(currentState);
      setDrawingHistory(newHistory);
      setCurrentHistoryIndex(newHistory.length - 1);
    }
  };

  const handleChangeSelectedColorCompare = (newColor: string) => {
    if (selectedShapeIdCompare) {
      const updatedShapes = shapesCompare.map(shape => 
        shape.id === selectedShapeIdCompare 
          ? { ...shape, color: newColor }
          : shape
      );
      setShapesCompare(updatedShapes);
      
      const currentState = { shapes: updatedShapes, selectedShapeId: selectedShapeIdCompare };
      const newHistory = drawingHistoryCompare.slice(0, currentHistoryIndexCompare + 1);
      newHistory.push(currentState);
      setDrawingHistoryCompare(newHistory);
      setCurrentHistoryIndexCompare(newHistory.length - 1);
    }
  };

  // Text tool handlers
  const handleTextToolClick = (pos: { x: number, y: number }) => {
    setTextPosition(pos);
    setIsTextModalVisible(true);
  };

  const handleTextToolClickCompare = (pos: { x: number, y: number }) => {
    setTextPosition(pos);
    setIsTextModalVisible(true);
  };

  const handleTextSubmit = () => {
    if (!textInput.trim()) return;

    const stage = document.querySelector('canvas');
    let normX = textPosition.x;
    let normY = textPosition.y;
    if (stage) {
      const rect = stage.getBoundingClientRect();
      normX = textPosition.x / rect.width;
      normY = textPosition.y / rect.height;
    }

    const newShape: Shape = {
      id: `shape-${Date.now()}`,
      tool: 'text',
      points: [normX, normY],
      color: drawingColor,
      text: textInput,
    };
    setShapes([...shapes, newShape]);
    setTextInput('');
    setIsTextModalVisible(false);
  };

  // Comment handlers
  const handleCommentSelect = (comment: CommentType) => {
    // Seçili comment'i güncelle
    setSelectedCommentId(comment.id);
    
    // Yorumun ait olduğu versiyonu seçili versiyon olarak ayarla
    if (comment.versionId && comment.versionId !== selectedVersion) {
      setSelectedVersion(comment.versionId);
    }
    
    // Player 1'i aktif yap
    setActivePlayer(1);
    
    // Diğer player'ın range'ini temizle
    setSelectedFrameRangeCompare(null);
    setSelectedRangeCompare(null);
    
    let shapesFromComment: Shape[] = [];
    try {
      if (comment.data) {
        const parsedData = JSON.parse(comment.data);
        shapesFromComment = parsedData.shapes || [];
      }
    } catch (error) {
      // Comment data parse error
    }

    setShapes(shapesFromComment);
    setIsPaused(true);
    setSelectedFrame(comment.startFrame);
    setSelectedFrameRange(comment.startFrame !== comment.endFrame ? [comment.startFrame, comment.endFrame] : null);
    
    const frameTime = comment.startFrame / FPS;
    setCurrentFrameIndex(comment.startFrame);
    setCurrentVideoTime(frameTime);
    
    const startTime = comment.startFrame / FPS;
    const endTime = comment.endFrame / FPS;
    
    setExternalRange([startTime, endTime]);
    setCurrentFrameRange(comment.startFrame === comment.endFrame ? `${comment.startFrame}` : `${comment.startFrame}-${comment.endFrame}`);
  };

  const handleCommentSelectCompare = (comment: CommentType) => {
    // Seçili comment'i güncelle
    setSelectedCommentId(comment.id);
    
    // Yorumun ait olduğu versiyonu compare versiyonu olarak ayarla
    if (comment.versionId && comment.versionId !== selectedVersionCompare) {
      setSelectedVersionCompare(comment.versionId);
    }
    
    // Player 2'yi aktif yap
    setActivePlayer(2);
    
    // Diğer player'ın range'ini temizle
    setSelectedFrameRange(null);
    setSelectedRange(null);
    
    let shapesFromComment: Shape[] = [];
    try {
      if (comment.data) {
        const parsedData = JSON.parse(comment.data);
        shapesFromComment = parsedData.shapes || [];
      }
    } catch (error) {
      // Comment data parse error
    }

    setShapesCompare(shapesFromComment);
    setIsPausedCompare(true);
    setSelectedFrameCompare(comment.startFrame);
    setSelectedFrameRangeCompare(comment.startFrame !== comment.endFrame ? [comment.startFrame, comment.endFrame] : null);
    
    const frameTime = comment.startFrame / FPS;
    setCurrentFrameIndexCompare(comment.startFrame);
    setCurrentVideoTimeCompare(frameTime);
    
    const startTime = comment.startFrame / FPS;
    const endTime = comment.endFrame / FPS;
    setSelectedRangeCompare([startTime, endTime]);
  };

  const handleCommentPinClick = (commentId: number) => {
    // Seçili comment'i güncelle
    setSelectedCommentId(commentId);
    
    // This will be handled by CommentSection's getFormattedComments
    const groupedComments = getAllComments();
    const allComments: CommentType[] = [];
    
    Object.values(groupedComments).forEach((dateComments: any) => {
      allComments.push(...dateComments);
    });
    
    const comment = allComments.find((c: CommentType) => c.id === commentId);
    if (comment) {
      handleCommentSelect(comment);
    }
  };

  // Get all comments helper (simplified version for pin clicks)
  const getAllComments = () => {
    if (!fileDetail?.versions) return {};
    
    let allComments: CommentType[] = [];
    
    if (showAllComments) {
      // Compare modunda ise sadece compare edilen versiyonların yorumlarını göster
      if (compareMode) {
        // Compare edilen iki versiyonun yorumlarını göster
        const compareVersions = [selectedVersion, selectedVersionCompare].filter(Boolean);
        fileDetail.versions.forEach((version: any) => {
          if (compareVersions.includes(version.id) && version.comments && Array.isArray(version.comments)) {
            const versionComments = version.comments.map((comment: any) => ({
              ...comment,
              versionId: version.id,
              versionName: version.name
            }));
            allComments = [...allComments, ...versionComments];
          }
        });
      } else {
        // Normal modda tüm versiyonların yorumlarını göster
        fileDetail.versions.forEach((version: any) => {
          if (version.comments && Array.isArray(version.comments)) {
            const versionComments = version.comments.map((comment: any) => ({
              ...comment,
              versionId: version.id,
              versionName: version.name
            }));
            allComments = [...allComments, ...versionComments];
          }
        });
      }
    } else {
      // showAllComments false ise aktif player'daki versiyonun yorumlarını göster
      if (compareMode) {
        // Compare modunda aktif player'a göre versiyon seç
        let targetVersion: number;
        if (activePlayer === 1) {
          targetVersion = selectedVersion;
        } else {
          targetVersion = selectedVersionCompare || selectedVersion;
        }
        
        const selectedVersionData = fileDetail.versions.find((v: any) => v.id === targetVersion);
        if (selectedVersionData?.comments && Array.isArray(selectedVersionData.comments)) {
          allComments = selectedVersionData.comments.map((comment: any) => ({
            ...comment,
            versionId: selectedVersionData.id,
            versionName: selectedVersionData.name
          }));
        }
      } else {
        // Normal modda seçili versiyonun yorumlarını göster
        const selectedVersionData = fileDetail.versions.find((v: any) => v.id === selectedVersion);
        if (selectedVersionData?.comments && Array.isArray(selectedVersionData.comments)) {
          allComments = selectedVersionData.comments.map((comment: any) => ({
            ...comment,
            versionId: selectedVersionData.id,
            versionName: selectedVersionData.name
          }));
        }
      }
    }
    
    const groupedComments = allComments.reduce((groups: any, comment: CommentType) => {
      const date = new Date(comment.createdAt).toLocaleDateString('tr-TR');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(comment);
      return groups;
    }, {});
    
    Object.keys(groupedComments).forEach(date => {
      groupedComments[date].sort((a: CommentType, b: CommentType) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    });
    
    return groupedComments;
  };

  // Get formatted comments for video player
  const getFormattedComments = () => {
    const groupedComments = getAllComments();
    const allComments: CommentType[] = [];
    
    Object.values(groupedComments).forEach((dateComments: any) => {
      allComments.push(...dateComments);
    });
    
    if (!allComments || allComments.length === 0) return [];
    
    return allComments.map((comment: CommentType) => ({
      id: comment.id,
      startFrame: comment.startFrame,
      endFrame: comment.endFrame,
      text: comment.text,
      shapes: comment.shapes || [],
      user: comment.user,
      isResolved: (comment as any).isResolved,
    }));
  };

  // File detail refresh handler
  const handleFileDetailRefresh = () => {
    if (fileDetail?.id) {
      getFileById(fileDetail.id).then(res => {
        if (res.success && res.data) {
          const previousVersionCount = fileDetail?.versions?.length || 0;
          const currentVersionCount = res.data.versions?.length || 0;
          
          setFileDetail(res.data);
          setCurrentVideoTime(0);
          setCurrentFrameIndex(null);
          
          // Sadece yeni version oluşturulduğunda yönlendir
          if (currentVersionCount > previousVersionCount) {
            // En yeni tarihli versiyonu bul
            const latestVersion = res.data.versions.reduce((latest: any, current: any) => {
              const latestDate = new Date(latest.createdAt);
              const currentDate = new Date(current.createdAt);
              return currentDate > latestDate ? current : latest;
            });
            
            if (latestVersion && latestVersion.path) {
              window.location.href = `/${latestVersion.path}`;
            }
          }
          // Comment yazıldığında mevcut version'da kal
        }
      });
    }
  };

  // Go back handler
  const handleGoBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  // Approval modal handlers
  const handleApprovalConfirm = async (changeSubentries: boolean) => {
    setApprovalLoading(true);
    try {
      await performStatusChange('Approved', changeSubentries);
      setIsApprovalModalVisible(false);
      } catch (error) {
      // performStatusChange içinde hata mesajı gösteriliyor
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApprovalCancel = () => {
    setIsApprovalModalVisible(false);
  };

  // Keyboard shortcuts
  // Status change functions
  const canChangeStatus = () => {
    if (!user) return false;
    return ['AccountManager', 'SuperAdmin', 'AccountOwner', 'InternalTeam', 'BrandAccountViewer'].includes(user.role);
  };

  const getCurrentStatus = () => {
    if (selectedVersion && fileDetail?.versions) {
      const selectedVersionData = fileDetail.versions.find((v: any) => v.id === selectedVersion);
      if (selectedVersionData) {
        return selectedVersionData.status || fileDetail.status || 'NeedsReview';
      }
    }
    return fileDetail?.status || 'NeedsReview';
  };

  const hasSubentries = () => {
    return fileDetail?.versions && Array.isArray(fileDetail.versions) && fileDetail.versions.length > 0;
  };

  const countUnresolvedComments = () => {
    let unresolvedComments = 0;
    
    if (fileDetail?.versions) {
      fileDetail.versions.forEach((version: any) => {
        if (version.comments && Array.isArray(version.comments)) {
          version.comments.forEach((comment: any) => {
            if (!comment.isResolved) {
              unresolvedComments++;
            }
          });
        }
      });
    }
    
    return unresolvedComments;
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!canChangeStatus()) {
      message.error('You do not have permission for this action.');
      return;
    }

    const currentStatus = getCurrentStatus();

    // Approved seçildiğinde resolve kontrolü yap
    if (newStatus === 'Approved') {
      const unresolvedComments = countUnresolvedComments();
      handleApprovalProcess(newStatus, unresolvedComments);
      return;
    }

    // Diğer statuslar için direkt servise git
    await performStatusChange(newStatus, false);
  };

  const handleApprovalProcess = (newStatus: string, unresolvedComments: number) => {
    if (newStatus === 'Approved') {
      setIsApprovalModalVisible(true);
    } else {
      performStatusChange('Updated', false);
    }
  };

  const performStatusChange = async (newStatus: string, changeSubentries: boolean) => {
    // setStatusLoading(true);
    try {
      const entries = [];
      
      if (fileDetail) {
        const versionId = selectedVersion || fileDetail.id;
        entries.push({
          id: versionId,
          entryType: selectedVersion ? 'Version' : (fileDetail.entryType || 'File'),
          entryStatus: newStatus,
          changeSubentries: changeSubentries
        });
      }

      if (entries.length > 0) {
        const response = await changeStatus(entries);
        if (response.success) {
          message.success('Status changed successfully.');
          // setIsStatusModalVisible(false);
          handleFileDetailRefresh();
        } else {
          if (response.message === 'Unauthorized') {
            message.error('You do not have permission to change status for these items.');
          } else {
            throw new Error(response.message || 'Status change failed');
          }
        }
      }
    } catch (error) {
      message.error('Network error or insufficient permissions. Please try again.');
    } finally {
      // setStatusLoading(false);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isDrawingMode) {
        if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          handleUndo();
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
          e.preventDefault();
          handleRedo();
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
          e.preventDefault();
          handleRedo();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawingMode, handleUndo, handleRedo]);
  // Computed values
  const canUndo = currentHistoryIndex > 0;
  const canRedo = currentHistoryIndex < drawingHistory.length - 1;
  const canUndoCompare = currentHistoryIndexCompare > 0;
  const canRedoCompare = currentHistoryIndexCompare < drawingHistoryCompare.length - 1;

  // Loading state
  if (loading) {
    return null;
  }

  // File not found state
  if (!fileDetail) {
    return (
      <MainLayout title={t('fileDetail.fileNotFound')} icon={<FileOutlined />}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div>{t('fileDetail.fileNotFound')}</div>
          <Button onClick={handleGoBack} style={{ marginTop: '16px' }}>
            {t('fileDetail.goBack')}
          </Button>
        </div>
      </MainLayout>
    );
  }

  // Versions page
  const isVersionsPage = location.pathname.endsWith('/versions');
  if (isVersionsPage) {
    if (loading) {
      return null;
    }
    if (!fileDetail) {
      return (
        <MainLayout title={t('fileDetail.fileNotFound')} icon={<FileOutlined />}>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div>{t('fileDetail.fileNotFound')}</div>
            <Button onClick={handleGoBack} style={{ marginTop: '16px' }}>
              {t('fileDetail.goBack')}
            </Button>
          </div>
        </MainLayout>
      );
    }
    return (
      <FileVersions 
        fileDetail={fileDetail} 
        projectName={projectName || ''}
        
      />
    );
  }

  return (
    <div className={styles.fileDetailWrapper} style={fileDetailThemeVars}>
      
      <div className={styles.fileDetailContainer}>
        <div className={styles.breadcrumbContainer}>
      <Breadcrumb />
        {canChangeStatus() && (
          <StatusSelector
            className={styles.statusSelector}
            onChange={handleStatusChange}
            disabled={false}
            userRole={user?.role}
            selectedItems={new Set([selectedVersion || fileDetail?.id])}
            selectedItemsDetails={[{
              id: selectedVersion || fileDetail?.id,
              name: fileDetail?.name || 'File',
              entryType: selectedVersion ? 'Version' : 'File',
              status: getCurrentStatus()
            }]}
            fileDetail={fileDetail}
            currentEntry={null}
            selectedVersion={selectedVersion}
            canMultiSelect={() => false}
          />
        )}
     </div>
        {/* Video Player Section */}
        <VideoPlayerSection
          fileDetail={fileDetail}
          versions={versions}
          versionsLoading={versionsLoading}
          fps={FPS}
          selectedCommentId={selectedCommentId}
          selectedVersion={selectedVersion}
          currentVideoUrl={currentVideoUrl}
          videoLoading={videoLoading}
          shapes={shapes}
          drawingTool={drawingTool}
          drawingColor={drawingColor}
          selectedShapeId={selectedShapeId}
          currentVideoTime={currentVideoTime}
          isPaused={isPaused}
          selectedRange={selectedRange}
          externalRange={externalRange}
          currentFrameIndex={currentFrameIndex ?? undefined}
          compareMode={compareMode}
          activePlayer={activePlayer}
          selectedVersionCompare={selectedVersionCompare}
          currentVideoUrlCompare={currentVideoUrlCompare}
          shapesCompare={shapesCompare}
          selectedShapeIdCompare={selectedShapeIdCompare}
          currentVideoTimeCompare={currentVideoTimeCompare}
          isPausedCompare={isPausedCompare}
          selectedRangeCompare={selectedRangeCompare}
          currentFrameIndexCompare={currentFrameIndexCompare ?? undefined}
          isDrawingMode={isDrawingMode}
          isDrawingToolboxOpen={isDrawingToolboxOpen}
          onVersionChange={(versionName) => {
            // Versiyon name'inden ID'yi bul
            const versionData = versions.find((v: any) => v.name === versionName);
            if (versionData) {
              setSelectedVersion(versionData.id);
            }
          }}
          onShapesChange={setShapes}
          onTextToolClick={handleTextToolClick}
          onShapeSelection={setSelectedShapeId}
          onDrawingComplete={handleDrawingComplete}
          onCurrentTimeChange={setCurrentVideoTime}
          onPlay={() => {
            setShapes([]);
            setCurrentFrameIndex(null);
            setIsPaused(false);
            setSelectedCommentId(null); // Comment seçimini sıfırla
          }}
          onSeek={() => {
            setShapes([]);
            setCurrentFrameIndex(null);
            setIsPaused(false);
            setSelectedCommentId(null); // Comment seçimini sıfırla
          }}
          onDurationChange={setVideoDuration}
          onRangeSelect={handleRangeSelect}
          onCompareVersionChange={(versionName) => {
            // Versiyon name'inden ID'yi bul
            const versionData = versions.find((v: any) => v.name === versionName);
            if (versionData) {
              setSelectedVersionCompare(versionData.id);
            }
          }}
          onShapesCompareChange={setShapesCompare}
          onTextToolClickCompare={handleTextToolClickCompare}
          onShapeSelectionCompare={setSelectedShapeIdCompare}
          onDrawingCompleteCompare={handleDrawingCompleteCompare}
          onCurrentTimeChangeCompare={setCurrentVideoTimeCompare}
          onPlayCompare={() => {
                                setShapesCompare([]);
            setCurrentFrameIndexCompare(null);
            setIsPausedCompare(false);
            setSelectedCommentId(null); // Comment seçimini sıfırla
                              }}
          onSeekCompare={() => {
                                setShapesCompare([]);
            setCurrentFrameIndexCompare(null);
            setIsPausedCompare(false);
            setSelectedCommentId(null); // Comment seçimini sıfırla
          }}
          onDurationChangeCompare={(duration) => {}}
          onRangeSelectCompare={handleRangeSelectCompare}
          onCompareModeChange={(newCompareMode: boolean) => {
            setCompareMode(newCompareMode);
            
            // Compare mode kapatıldığında gerekli state'leri sıfırla
            if (!newCompareMode) {
              setShowAllComments(false);
              setSelectedCommentId(null);
              setActivePlayer(1);
              // Compare player'ın state'lerini de sıfırla
              setShapesCompare([]);
              setSelectedShapeIdCompare(null);
              setCurrentFrameIndexCompare(null);
              setSelectedFrameCompare(null);
              setSelectedFrameRangeCompare(null);
              setSelectedRangeCompare(null);
              setCurrentVideoTimeCompare(0);
              setIsPausedCompare(false);
            }
          }}
          onActivePlayerChange={setActivePlayer}
          onCommentPinClick={handleCommentPinClick}
          getFormattedComments={getFormattedComments}
          onCompareModeClose={() => {
            // Compare mode kapatıldığında ek temizlik işlemleri
            setShowAllComments(false);
            setSelectedCommentId(null);
            setActivePlayer(1);
          }}
        />
        <div className={styles.footerActions}>
        <FooterActions 
        isHeaderless={true}
        onCompare={() => {
          if (!compareMode) {
            setCompareMode(true);
          }
          // Compare modal'ı açıldığında seçili commentleri sıfırla
          resetAllStates();
        }}
        onDelete={handleDelete}
        onDownload={handleDownload}
        onCopyUrl={handleCopyUrl}
        // Status değişikliği sonrası FileDetail'ı yenile
        
        fileDetail={fileDetail}
        selectedVersion={compareMode ? (selectedVersionCompare ?? undefined) : selectedVersion}
        selectedItems={selectedItems}
        selectedItemsDetails={selectedItemsDetails}
        currentEntry={compareMode && selectedVersionCompare ? 
          versions.find((v: any) => v.id === selectedVersionCompare) : 
          versions.find((v: any) => v.id === selectedVersion)
        }
        style={{
          transition: footerMounted ? 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out' : 'none',
        }}
      />
        </div>
      </div>

      {/* Comment Section */}
      <div className={styles.commentContainer}>
        <CommentSection
          fileDetail={fileDetail}
          versions={versions}
          versionsLoading={versionsLoading}
          selectedVersion={selectedVersion}
          selectedVersionCompare={selectedVersionCompare}
          selectedCommentId={selectedCommentId}
          showAllComments={showAllComments}
          onShowAllCommentsChange={setShowAllComments}
          onVersionChange={(versionName) => {
            // Versiyon name'inden ID'yi bul
            const versionData = versions.find((v: any) => v.name === versionName);
            if (versionData) {
              setSelectedVersion(versionData.id);
            }
          }}
          canComment={canComment}
          canShowAllComments={canShowAllComments}
          canResolve={canResolve}
          userRole={user?.role}
          onAddNewFile={() => setIsAddNewFileModalOpen(true)}
          compareMode={compareMode}
          activePlayer={activePlayer}
          currentFrameRange={currentFrameRange}
          currentFrameRangeCompare={currentFrameRangeCompare}
          shapes={shapes}
          shapesCompare={shapesCompare}
          drawingTool={drawingTool}
          drawingColor={drawingColor}
          selectedShapeId={selectedShapeId}
          selectedShapeIdCompare={selectedShapeIdCompare}
          onShapesChange={setShapes}
          onShapesCompareChange={setShapesCompare}
                    onToolChange={setDrawingTool}
                    onColorChange={setDrawingColor}
          onClearShapes={() => activePlayer === 1 ? setShapes([]) : setShapesCompare([])}
          onSave={() => {}}
          onDeleteSelected={handleDeleteSelected}
          onDeleteSelectedCompare={handleDeleteSelectedCompare}
          onChangeSelectedColor={handleChangeSelectedColor}
          onChangeSelectedColorCompare={handleChangeSelectedColorCompare}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onUndoCompare={handleUndoCompare}
          onRedoCompare={handleRedoCompare}
          canUndo={canUndo}
          canRedo={canRedo}
          canUndoCompare={canUndoCompare}
          canRedoCompare={canRedoCompare}
          currentVideoTime={currentVideoTime}
          currentFps={currentFps}
          videoDuration={videoDuration}
          selectedFrame={selectedFrame}
          selectedFrameRange={selectedFrameRange}
          selectedFrameCompare={selectedFrameCompare}
          selectedFrameRangeCompare={selectedFrameRangeCompare}
          onCommentSelect={handleCommentSelect}
          onCommentSelectCompare={handleCommentSelectCompare}
          onGuestCommentSubmit={handleFileDetailRefresh}
          onFileDetailRefresh={handleFileDetailRefresh}
          closeDrawingPopover={closeDrawingPopover}
          onCloseDrawingPopover={setCloseDrawingPopover}
          isDrawingToolboxOpen={isDrawingToolboxOpen}
          onDrawingToolboxOpenChange={setIsDrawingToolboxOpen}
          onResetFrameSelection={() => {
            setSelectedFrame(null);
            setSelectedFrameRange(null);
            setSelectedFrameCompare(null);
            setSelectedFrameRangeCompare(null);
            setCurrentFrameRange('');
            setCurrentFrameRangeCompare('');
          }}
                  />
                </div>

      {/* Text Modal */}
      <Modal
        title={t('Add Text')}
        open={isTextModalVisible}
        onOk={handleTextSubmit}
        onCancel={() => setIsTextModalVisible(false)}
        okText={t('Add')}
        cancelText={t('Cancel')}
      >
        <Input 
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          placeholder={t('Enter your text...')}
          onPressEnter={handleTextSubmit}
        />
      </Modal>

      {/* Add New File Modal */}
              <AddNewFileModal
          isOpen={isAddNewFileModalOpen}
          onClose={() => setIsAddNewFileModalOpen(false)}
          projectId={fileDetail?.projectId}
        parentId={fileDetail?.id}
          isFromFileDetail={true}
          fileId={fileDetail?.id}
          fileDetail={fileDetail}
          editData={fileDetail}
                    onSuccess={(newVersionData) => {
            setIsAddNewFileModalOpen(false);
            // Yeni versiyon yüklendiyse en son versiyona navigate et
            if (newVersionData?.shouldNavigateToLatest) {
              // Sayfayı yenile ve en son versiyonu seç
              handleFileDetailRefresh();
            }
        }}
      />
      
     
      
      {/* Footer Actions */}
      <div className={styles.footerActionsMobile}>
      <FooterActions 
        isHeaderless={true}
        onCompare={() => {
          if (!compareMode) {
            setCompareMode(true);
          }
          // Compare modal'ı açıldığında seçili commentleri sıfırla
          resetAllStates();
        }}
        onDelete={handleDelete}
        onDownload={handleDownload}
        onCopyUrl={handleCopyUrl}
        // Status değişikliği sonrası FileDetail'ı yenile
        
        fileDetail={fileDetail}
        selectedVersion={compareMode ? (selectedVersionCompare ?? undefined) : selectedVersion}
        selectedItems={selectedItems}
        selectedItemsDetails={selectedItemsDetails}
        currentEntry={compareMode && selectedVersionCompare ? 
          versions.find((v: any) => v.id === selectedVersionCompare) : 
          versions.find((v: any) => v.id === selectedVersion)
        }
        style={{
          transition: footerMounted ? 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out' : 'none',
        }}
      />
      </div>
      

      {/* Approval Modal */}
      <ApprovalModal
        visible={isApprovalModalVisible}
        onCancel={handleApprovalCancel}
        onConfirm={handleApprovalConfirm}
        loading={approvalLoading}
      />
    </div>
  );
};

export default FileDetail; 